import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import LoadingPage from '@/components/ui/LoadingPage';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import IssueReportApi from '@/api/issueReportApi';
import { useDeleteIssueReport, useIssueReport, useUpdateIssueReport } from '@/hooks/useIssueReports';
import { useIsAdmin } from '@/hooks/useAdmin';
import { IssueSeverity, IssueStatus } from '@/schema/issueReport';
import { SectionCard } from '../components/SectionCard';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SEVERITY_STYLES, STATUS_LABELS, STATUS_STYLES } from '../components/issueStyles';
import { EM_DASH, formatDateTime } from '../lib/format';
import { useAdminCaseSelection } from '../hooks/useAdminCaseSelection';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className="break-words text-sm text-white/85">{value || EM_DASH}</p>
    </div>
  );
}

/** Screenshots need the auth header, so they are fetched and shown via an object URL. */
function Screenshot({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    IssueReportApi.getScreenshot(id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => setUrl(null));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  if (!url) return <p className="text-sm text-white/45">Loading screenshot…</p>;

  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt="Screenshot attached to the report"
        className="max-h-96 w-full rounded-xl border border-content-highlight/40 object-contain"
      />
    </a>
  );
}

export function AdminIssueDetail() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const { openCase } = useAdminCaseSelection();
  const { data, isLoading, isError } = useIssueReport(issueId);
  const update = useUpdateIssueReport();
  const remove = useDeleteIssueReport();
  const { isSuperAdmin } = useIsAdmin();
  const [note, setNote] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useDocumentTitle(data ? `Admin - ${data.title}` : 'Admin - Issue');

  useEffect(() => {
    setNote(data?.resolutionNote ?? '');
  }, [data?.resolutionNote]);

  if (isLoading) return <LoadingPage loadingText="Loading report" />;
  if (isError || !data) return <p className="p-6 text-sm text-white/60">Could not load this report.</p>;

  const patch = (payload: Parameters<typeof update.mutate>[0]['data']) => {
    update.mutate(
      { id: data.id, data: payload },
      { onError: () => toast.error('Could not update the report') },
    );
  };

  const context = (() => {
    if (!data.context) return null;
    try {
      return JSON.stringify(JSON.parse(data.context), null, 2);
    } catch {
      return data.context;
    }
  })();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="ghost-light" size="icon" onClick={() => navigate('/admin/issues')}>
          <ArrowLeftIcon />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold text-white">{data.title}</h2>
          <p className="truncate text-sm text-white/50">
            {data.reportedByEmail ?? 'Unknown reporter'} · {formatDateTime(data.createdAt)}
          </p>
        </div>
        <span className={SEVERITY_STYLES[data.severity]}>{data.severity}</span>
        <span className={STATUS_STYLES[data.status]}>{STATUS_LABELS[data.status]}</span>
        {isSuperAdmin && (
          <Button
            type="button"
            variant="ghost-danger"
            size="sm"
            onClick={() => setIsDeleting(true)}
            title="Delete this report"
          >
            <Trash2Icon />
            Delete
          </Button>
        )}
      </div>

      {isDeleting && (
        <ConfirmDialog
          title="Delete report"
          confirmLabel="Delete report"
          isPending={remove.isPending}
          error={remove.error?.message}
          onClose={() => setIsDeleting(false)}
          onConfirm={() =>
            remove.mutate(data.id, {
              onSuccess: () => {
                toast.success('Report deleted');
                navigate('/admin/issues');
              },
            })
          }
        >
          <p>
            <strong>{data.title}</strong> goes for good, together with its screenshot, stack
            trace and diagnostic context.
          </p>
          <p className="text-white/50">Closing it keeps all of that and takes it off the open list.</p>
        </ConfirmDialog>
      )}

      <SectionCard title="Triage">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <Select value={data.status} onValueChange={(value) => patch({ status: value as IssueStatus })}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(IssueStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Select
              value={data.severity}
              onValueChange={(value) => patch({ severity: value as IssueSeverity })}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(IssueSeverity).map((severity) => (
                    <SelectItem key={severity} value={severity}>
                      {severity}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What was the cause, what was done about it?"
              rows={3}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost-light"
                size="sm"
                disabled={note === (data.resolutionNote ?? '') || update.isPending}
                onClick={() => patch({ resolutionNote: note })}
              >
                Save note
              </Button>
            </div>
          </div>

          {data.resolvedAt ? (
            <p className="text-xs text-white/45">
              Closed {formatDateTime(data.resolvedAt)} by {data.resolvedByEmail ?? EM_DASH}
            </p>
          ) : null}
        </div>
      </SectionCard>

      {data.description ? (
        <SectionCard title="Description">
          <p className="whitespace-pre-wrap text-sm text-white/85">{data.description}</p>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Environment"
        description="Most 3D failures are driver-specific, so this is usually where the answer is."
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field label="Route" value={data.route} />
          <Field label="App version" value={data.appVersion} />
          <Field label="Platform" value={data.platform} />
          <Field label="Screen" value={data.screenInfo} />
          <Field label="GPU vendor" value={data.gpuVendor} />
          <Field label="GPU renderer" value={data.gpuRenderer} />
          <Field label="Patient" value={data.patientId} />
          <Field label="Scan" value={data.scanId} />
          <Field label="Clinic" value={data.clinicName} />
        </div>
        <div className="mt-4">
          <Field label="User agent" value={data.userAgent} />
        </div>
        {data.scanId && data.patientId ? (
          <div className="mt-4">
            <Button
              type="button"
              variant="ghost-light"
              size="sm"
              onClick={() => openCase(data.patientId!, data.scanId)}
            >
              Open the affected scan
            </Button>
          </div>
        ) : null}
      </SectionCard>

      {data.hasScreenshot ? (
        <SectionCard title="Screenshot">
          <Screenshot id={data.id} />
        </SectionCard>
      ) : null}

      {data.errorStack ? (
        <SectionCard title="Stack trace">
          <pre className="max-h-80 overflow-auto rounded-xl border border-content-highlight/40 bg-content-dark/70 p-3 text-xs text-white/75">
            {data.errorStack}
          </pre>
        </SectionCard>
      ) : null}

      {context ? (
        <SectionCard title="Captured context" description="Dev log tail and extra diagnostics.">
          <pre className="max-h-96 overflow-auto rounded-xl border border-content-highlight/40 bg-content-dark/70 p-3 text-xs text-white/75">
            {context}
          </pre>
        </SectionCard>
      ) : null}
    </div>
  );
}

export default AdminIssueDetail;
